/**
 * CR150: Preview Status Pill with Quick-Switch Popover
 * 
 * Displays current preview mode and allows quick switching via popover.
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileEdit, Eye, Globe, ChevronDown } from "lucide-react";
import type { PreviewSource } from "@/hooks/useLayoutPreviewSource";

interface PreviewStatusPillProps {
  source: PreviewSource;
  hasActivePreset: boolean;
  onSourceChange: (source: PreviewSource) => void;
}

const SOURCE_CONFIG: Record<PreviewSource, { label: string; icon: typeof FileEdit; variant: "secondary" | "outline" | "default" }> = {
  draft: { label: "Previewing draft", icon: FileEdit, variant: "secondary" },
  published_staging: { label: "Previewing published", icon: Eye, variant: "outline" },
  published_live: { label: "Previewing published", icon: Globe, variant: "default" },
};

export function PreviewStatusPill({ source, hasActivePreset, onSourceChange }: PreviewStatusPillProps) {
  const config = SOURCE_CONFIG[source];
  const Icon = config.icon;

  // Override label if published mode has no active preset
  const displayLabel = (source !== "draft" && !hasActivePreset) 
    ? "No published preset active" 
    : config.label;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge 
          variant={config.variant} 
          className="text-[10px] cursor-pointer hover:opacity-80 transition-opacity gap-1 pr-1"
        >
          <Icon className="h-3 w-3" />
          {displayLabel}
          <ChevronDown className="h-2.5 w-2.5 ml-0.5" />
        </Badge>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-1">
        <div className="space-y-0.5">
          <Button
            variant={source === "draft" ? "secondary" : "ghost"}
            size="sm"
            className="w-full justify-start text-xs h-8"
            onClick={() => onSourceChange("draft")}
          >
            <FileEdit className="h-3.5 w-3.5 mr-2" />
            Draft
          </Button>
          <Button
            variant={source === "published_staging" ? "secondary" : "ghost"}
            size="sm"
            className="w-full justify-start text-xs h-8"
            onClick={() => onSourceChange("published_staging")}
          >
            <Eye className="h-3.5 w-3.5 mr-2" />
            Published (Staging)
          </Button>
          <Button
            variant={source === "published_live" ? "secondary" : "ghost"}
            size="sm"
            className="w-full justify-start text-xs h-8"
            onClick={() => onSourceChange("published_live")}
          >
            <Globe className="h-3.5 w-3.5 mr-2" />
            Published (Live)
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
